import { useApi } from '@/api';
import { TableSkeleton } from '@/components';
import {
  Heading,
  CryptoIcon,
  Flex,
  CurrencyValue,
  useTableProps,
  PercentageValue,
  DataTable,
  RelativeDateTime,
  Box
} from '@blockanalitica/ui';
import { TableColumn } from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';

export default function TakesTable({ ilk, auction_id }) {
  const { pagination, sorting } = useTableProps({
    pageSize: 10,
    initialSorting: '+datetime',
    filtersDebounceTime: 50
  });
  const [data, error] = useApi({
    url: `/liquidations/${ilk}/${auction_id}/`,
    queryParams: {
      p: pagination.currentPage,
      p_size: pagination.pageSize,
      order: sorting.currentSorting,
      event: 'Take'
    }
  });

  if (error) {
    throw error;
  }

  if (data) {
    const symbol = data['results'][0].symbol;
    const columns: TableColumn[] = [
      {
        header: (
          <Flex
            display="flex"
            flexDirection="row"
            alignItems="center"
            gap="3xs">
            Sold Collateral
            <CryptoIcon name={symbol} size="0" />
          </Flex>
        ),
        getCell: (row) => <CurrencyValue value={row.sold_collateral} />,
        cellAlign: 'flex-start',
        headerAlign: 'flex-start',
        sortingField: 'sold_collateral'
      },
      {
        header: (
          <Flex
            display="flex"
            flexDirection="row"
            alignItems="center"
            gap="3xs">
            Recovered Debt
            <CryptoIcon name="DAI" size="0" />
          </Flex>
        ),
        getCell: (row) => <CurrencyValue value={row.recovered_debt} />,
        cellAlign: 'flex-end',
        headerAlign: 'flex-end',
        sortingField: 'recovered_debt'
      },
      {
        header: 'Collateral Price',
        getCell: (row) => (
          <CurrencyValue
            value={row.avg_price}
            options={{
              currency: 'USD'
            }}
          />
        ),
        cellAlign: 'flex-end',
        headerAlign: 'flex-end',
        sortingField: 'avg_price'
      },
      {
        header: 'OSM Price',
        getCell: (row) => (
          <CurrencyValue
            value={row.osm_price}
            options={{
              currency: 'USD',
              notation: 'standard'
            }}
          />
        ),
        cellAlign: 'flex-end',
        headerAlign: 'flex-end',
        sortingField: 'osm_price'
      },
      {
        header: 'Market Price',
        getCell: (row) => (
          <CurrencyValue
            value={row.osm_price}
            options={{
              currency: 'USD',
              notation: 'standard'
            }}
          />
        ),
        cellAlign: 'flex-end',
        headerAlign: 'flex-end',
        sortingField: 'osm_price'
      },
      {
        header: 'Settled OSM Price',
        getCell: (row) => (
          <PercentageValue
            value={row.settled_price}
            options={{
              formatPercentageOptions: { maximumFractionDigits: 2 }
            }}
          />
        ),
        cellAlign: 'flex-end',
        headerAlign: 'flex-end',
        sortingField: 'settled_price'
      },
      {
        header: 'Settled Market Price',
        getCell: (row) => (
          <PercentageValue
            value={row.settled_price}
            options={{
              formatPercentageOptions: { maximumFractionDigits: 2 }
            }}
          />
        ),
        cellAlign: 'flex-end',
        headerAlign: 'flex-end',
        sortingField: 'settled_price'
      },
      {
        header: (
          <Flex
            display="flex"
            flexDirection="row"
            alignItems="center"
            gap="3xs">
            Debt
            <CryptoIcon name="DAI" size="0" />
          </Flex>
        ),
        getCell: (row) => <CurrencyValue value={row.debt} />,
        cellAlign: 'flex-end',
        headerAlign: 'flex-end',
        sortingField: 'debt'
      },
      {
        header: (
          <Flex
            display="flex"
            flexDirection="row"
            alignItems="center"
            gap="3xs">
            Available Collateral
            <CryptoIcon
              name={symbol}
              address={data.collateral_address}
              size="0"
            />
          </Flex>
        ),
        getCell: (row) => <CurrencyValue value={row.available_collateral} />,
        cellAlign: 'flex-end',
        headerAlign: 'flex-end',
        sortingField: 'available_collateral'
      },
      {
        header: 'Time',
        getCell: (row) => (
          <Box color="muted">
            <RelativeDateTime value={row.datetime} />
          </Box>
        ),
        cellAlign: 'flex-end',
        headerAlign: 'flex-end',
        sortingField: 'datetime'
      }
    ];

    return (
      <>
        <Heading size="2">Takes</Heading>
        <DataTable
          rows={data['results']}
          columns={columns}
          getKey={(row) => `${row.ilk}-${row.auction_id}`}
          sorting={sorting}
          pagination={{
            ...pagination,
            totalRecords: data.count
          }}
        />
      </>
    );
  }
  return <TableSkeleton />;
}
